import React, { FC, useMemo, useState } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Trans, useTranslation } from "react-i18next";
import { useI18next } from "gatsby-plugin-react-i18next";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import ReCAPTCHA from "react-google-recaptcha";
import { Checkbox, Input, Dropdown } from "@app/components/common/form";
import { Button } from "@app/components/common";
import { ModalState } from "@app/enums/modal";
import { RecaptchaVersion } from "@app/enums/recaptcha";
import { CustomError } from "@app/enums/errors";
import { useModal } from "@app/hooks";
import { ContestContactFormData } from "./types";
import { useValidationSchema } from "./useValidationSchema";
import { useContact } from "./useContact";
import { RECAPTCHA_ACTION, INITIAL_VALUES, UNDEFINED_VALUE } from "./constants";

type DropdownOption = {
  title: string;
  value: string;
};

type Props = {
  formId: string;
};

export const ContactForm: FC<Props> = ({ formId }) => {
  const { t } = useTranslation("contest-contact-form");
  const { executeRecaptcha } = useGoogleReCaptcha();
  const { language } = useI18next();
  const { sendContactRequest } = useContact();
  const { onChangeModalState } = useModal();
  const [isRecaptchaError, setIsRecaptchaError] = useState(false);

  const validationSchema = useValidationSchema(t);

  const methods = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: INITIAL_VALUES,
  });

  const GENDER_OPTIONS = useMemo(
    () =>
      (t("gender_options", { returnObjects: true }) as DropdownOption[]).map(
        (option) => ({
          label: option.title,
          value: option.value,
        }),
      ),
    [],
  );

  const onSubmit = async (values: ContestContactFormData) => {
    try {
      const recaptchaToken = await executeRecaptcha(RECAPTCHA_ACTION);

      await sendContactRequest(values, {
        action: RECAPTCHA_ACTION,
        token: recaptchaToken,
        location: window?.location?.href ?? UNDEFINED_VALUE,
        title: document?.title ?? UNDEFINED_VALUE,
        language: language,
        formId: formId,
      });
      methods.reset();
      onChangeModalState(ModalState.SUCCESS);
    } catch (error) {
      if (error.name === CustomError.RECAPTCHA_NOT_ENOUGH_SCORES) {
        setIsRecaptchaError(true);

        return;
      }

      onChangeModalState(ModalState.ERROR);
      methods.reset();
    }
  };

  const onRecaptchaSubmit = async (token: string) => {
    try {
      const formData = methods.getValues() as ContestContactFormData;

      await sendContactRequest(formData, {
        action: RECAPTCHA_ACTION,
        token: token,
        location: window?.location?.href ?? UNDEFINED_VALUE,
        title: document?.title ?? UNDEFINED_VALUE,
        language: language,
        formId: formId,
        recaptchaVersion: RecaptchaVersion.V2,
      });
      onChangeModalState(ModalState.SUCCESS);
    } catch (_error) {
      onChangeModalState(ModalState.ERROR);
    } finally {
      methods.reset();
      setIsRecaptchaError(false);
    }
  };

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={methods.handleSubmit(onSubmit)}
        className="[&_a:hover]:text-primary-content relative space-y-5"
      >
        <Input name="firstName" label={`${t("first_name")} *`} />
        <Input name="lastName" label={`${t("last_name")} *`} />
        <Input name="email" label={`${t("email")} *`} type="email" />
        <Dropdown
          name="gender"
          label={`${t("gender")} *`}
          options={GENDER_OPTIONS}
          dropdownBgVariant="grey"
        />
        <Input name="birth" label={`${t("birth")} *`} type="date" />
        <div className="space-y-2.5">
          <Checkbox name="isSwissResidentConfirmed" label={t("swiss_agree")} />
          <Checkbox
            name="isPrivacyPolicyConfirmed"
            label={
              <Trans
                t={t}
                i18nKey="agree"
                components={[
                  <a
                    key="privacy-policy"
                    href="/legal/privacy-policy"
                    target="_blank"
                    rel="noopener noreferrer"
                  />,
                ]}
              />
            }
          />
        </div>
        <div className="mt-5 flex flex-row items-center gap-x-3">
          <Button
            label={t("form_submit")}
            isDisabled={methods.formState.isSubmitting}
            isLoading={methods.formState.isSubmitting}
            type="submit"
          />
        </div>
        {isRecaptchaError ? (
          <div className="bg-dark/75 absolute top-0 left-0 flex h-full w-full items-center justify-center">
            <ReCAPTCHA
              sitekey={process.env.GATSBY_RECAPTCHA_V2_KEY}
              onChange={onRecaptchaSubmit}
            />
          </div>
        ) : null}
      </form>
    </FormProvider>
  );
};
